import { type ReactNode, useCallback, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Info, LogOut, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useSystemVersion } from '@/hooks/useSystemVersion';
import { cn } from '@/lib/utils';
import VersionInfoDialog from '@/components/system-version/VersionInfoDialog';
import {
  NAV_ITEMS,
  selectVisibleNavItems,
} from './navigationItems';

export type { NavItem } from './navigationItems';
export { NAV_ITEMS, selectVisibleNavItems } from './navigationItems';

export interface DashboardLayoutProps {
  children: ReactNode;
  onToggleSidebar?: () => void;
  isSidebarCollapsed?: boolean;
}

export default function DashboardLayout({
  children,
  onToggleSidebar,
  isSidebarCollapsed = false,
}: DashboardLayoutProps) {
  const [location] = useLocation();
  const { session, signOut, permissions } = useAuth();
  const versionInfo = useSystemVersion();
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);

  const visibleNavItems = selectVisibleNavItems(NAV_ITEMS, permissions);

  const openVersionDialog = useCallback(() => {
    setVersionDialogOpen(true);
    void versionInfo.fetch();
  }, [versionInfo]);

  const closeVersionDialog = useCallback(() => {
    setVersionDialogOpen(false);
  }, []);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside
        className={cn(
          'flex flex-col border-r bg-card transition-all',
          isSidebarCollapsed ? 'w-16' : 'w-60',
        )}
        aria-label="主导航"
      >
        <div className="flex h-14 items-center border-b px-4 font-semibold">
          {isSidebarCollapsed ? '金' : '金专 · 管理后台'}
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const active = item.prefixMatch
              ? location.startsWith(item.to)
              : location === item.to;
            return (
              <Link
                key={item.to}
                href={item.to}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  active
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" aria-hidden />
                {!isSidebarCollapsed && (
                  <span className="flex-1">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>
        <div
          className={cn(
            'border-t p-2',
            isSidebarCollapsed ? 'flex flex-col gap-1' : 'flex items-center gap-1',
          )}
        >
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'gap-2',
              isSidebarCollapsed ? 'w-full justify-center' : 'flex-1 justify-start',
            )}
            onClick={signOut}
          >
            <LogOut className="h-4 w-4" />
            {!isSidebarCollapsed && '退出登录'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'gap-2',
              isSidebarCollapsed ? 'w-full justify-center' : 'flex-1 justify-start',
            )}
            onClick={openVersionDialog}
            aria-label="版本信息"
            data-testid="sidebar-version-info-button"
          >
            <Info className="h-4 w-4" />
            {!isSidebarCollapsed && '版本信息'}
          </Button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b bg-card px-4">
          <div className="flex items-center gap-2">
            {onToggleSidebar ? (
              <Button variant="ghost" size="icon" aria-label="切换侧栏" onClick={onToggleSidebar}>
                <Menu className="h-4 w-4" />
              </Button>
            ) : null}
            <h1 className="text-sm font-medium text-muted-foreground">
              {currentTitle(location)}
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {session?.source === 'dev-mock' && import.meta.env.DEV ? (
              <span className="rounded border border-amber-400 bg-amber-50 px-2 py-0.5 text-amber-700">
                DEV MOCK 会话
              </span>
            ) : null}
            {/* Display subject only — do NOT expose tenantId/orgId */}
            <span>{session ? session.subject : '未登录'}</span>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
      {versionDialogOpen ? (
        <VersionInfoDialog
          data={versionInfo.data}
          loading={versionInfo.loading}
          errorMessage={versionInfo.error?.message ?? null}
          onClose={closeVersionDialog}
          onRefresh={() => {
            void versionInfo.fetch();
          }}
        />
      ) : null}
    </div>
  );
}

function currentTitle(path: string): string {
  if (path.startsWith('/crm/orders')) return 'CRM 订单';
  if (path.startsWith('/crm/customers')) return '客户管理';
  if (path.startsWith('/crm/channel-partners')) return '渠道合作方';
  if (path.startsWith('/crm')) return '客户与订单';
  if (path.startsWith('/channel-roi')) return '渠道 ROI';
  if (path.startsWith('/proposals')) return '方案与报价';
  if (path.startsWith('/finance')) return '财务结算';
  if (path.startsWith('/operations/work-orders')) return '服务工单';
  if (path.startsWith('/scheduling')) return '预约排班';
  if (path.startsWith('/lab')) return '实验室与回输';
  if (path.startsWith('/inventory')) return '物料库存';
  if (path.startsWith('/compliance')) return '政策合规';
  if (path.startsWith('/task-center')) return '任务中心';
  if (path.startsWith('/customer-documents')) return '客户资料审核';
  if (path.startsWith('/intake-assessment')) return '接诊评估';
  if (path.startsWith('/health-report-publications')) return '健康报告发布';
  if (path.startsWith('/complaints')) return '投诉与纠纷';
  if (path.startsWith('/risk/read-model')) return '风险事件读模型';
  if (path.startsWith('/risk')) return '风险事件读模型';
  if (path.startsWith('/hr/imports')) return '员工花名册导入';
  if (path.startsWith('/hr')) return '员工花名册';
  if (path.startsWith('/scan')) return '统一扫码';
  if (path.startsWith('/ai-model-center')) return 'AI 模型中心';
  if (path.startsWith('/sms')) return '短信模板与日志';
  const item = NAV_ITEMS.find((n) => n.to === path);
  return item?.label ?? '页面';
}
